import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Loader2, Check } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reviews({ productSlug }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", title: "", body: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`, { params: { product_slug: productSlug } });
      setSummary(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); setSummary(null); fetchReviews(); /* eslint-disable-next-line */ }, [productSlug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { product_slug: productSlug, rating: form.rating, name: form.name, email: form.email || null, title: form.title || null, body: form.body });
      toast({ title: "Thanks for your review!" });
      setSubmitted(true);
      setForm({ name: "", email: "", title: "", body: "", rating: 5 });
      fetchReviews();
    } catch (err) {
      toast({ title: err.response?.data?.detail || "Could not submit", variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-10 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-7">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="kicker">Reviews</p>
          <div className="flex items-center gap-3 mt-2">
            <h3 className="font-serif text-3xl text-[color:var(--dms-navy)]">{summary?.average?.toFixed(1) || "—"}</h3>
            <StarRow rating={Math.round(summary?.average || 0)} />
            <span className="text-sm text-[color:var(--dms-navy)]/60">({summary?.count || 0})</span>
          </div>
        </div>
        {!showForm && !submitted && (
          <button onClick={() => setShowForm(true)} className="btn-peach">Write a review</button>
        )}
      </div>

      {showForm && !submitted && (
        <form onSubmit={submit} className="mt-6 border-t border-[color:var(--dms-navy)]/10 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Your rating</p>
            <div className="mt-2 flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} className="p-1" aria-label={`${n} stars`}>
                  <Star className={`w-7 h-7 ${form.rating >= n ? "fill-[color:var(--dms-peach)] text-[color:var(--dms-peach)]" : "text-[color:var(--dms-navy)]/20"}`} />
                </button>
              ))}
            </div>
          </div>
          <Field label="Your name" required><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
          <Field label="Email (optional)"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
          <Field label="Title (optional)" className="sm:col-span-2"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="e.g. Soft, true to size and the design is perfect" /></Field>
          <Field label="Your review" required className="sm:col-span-2"><textarea required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputCls} placeholder="Share your experience with this product…" /></Field>
          <div className="sm:col-span-2 flex items-center justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="text-sm text-[color:var(--dms-navy)]/60 hover:text-[color:var(--dms-navy)]">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-peach">{submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting…</> : "Submit review"}</button>
          </div>
        </form>
      )}

      {submitted && (
        <div className="mt-6 border-t border-[color:var(--dms-navy)]/10 pt-6 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center flex-shrink-0"><Check className="w-5 h-5 text-[color:var(--dms-navy)]" /></div>
          <div><p className="font-serif text-lg text-[color:var(--dms-navy)]">Thank you!</p><p className="text-sm text-[color:var(--dms-navy)]/60">Your review is now live on this page.</p></div>
        </div>
      )}

      <div className="mt-6 border-t border-[color:var(--dms-navy)]/10 pt-6">
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--dms-peach)] mx-auto" /></div>
        ) : (summary?.reviews || []).length === 0 ? (
          <div className="py-8 text-center text-sm text-[color:var(--dms-navy)]/60">No reviews yet. Be the first!</div>
        ) : (
          <ul className="space-y-5">
            {summary.reviews.map((r) => (
              <li key={r.id} className="border-b border-[color:var(--dms-navy)]/5 last:border-0 pb-5 last:pb-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <StarRow rating={r.rating} />
                  <p className="font-serif text-[color:var(--dms-navy)] text-lg">{r.title || " "}</p>
                </div>
                <p className="mt-2 text-[color:var(--dms-navy)]/80 text-sm leading-relaxed whitespace-pre-wrap">{r.body}</p>
                <p className="mt-2 text-xs text-[color:var(--dms-navy)]/55"><span className="font-semibold text-[color:var(--dms-navy)]/70">{r.name}</span> · {new Date(r.created_at).toLocaleDateString()}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StarRow({ rating = 0 }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${rating >= n ? "fill-[color:var(--dms-peach)] text-[color:var(--dms-peach)]" : "text-[color:var(--dms-navy)]/20"}`} />
      ))}
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-[color:var(--dms-navy)] focus:outline-none focus:border-[color:var(--dms-peach)] transition";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}{required && <span className="text-[color:var(--dms-peach)]"> *</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
